import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { resetAriadne } from './services/ariadne';
import ShareModal from './components/modals/ShareModal';
import CommentModal from './components/modals/CommentModal';
import AddAuthorModal from './components/modals/AddAuthorModal';
import AddOrganizationModal from './components/modals/AddOrganizationModal';
import AddLinkModal from './components/modals/AddLinkModal';
import ChangeAvatarModal from './components/modals/ChangeAvatarModal';
import EditProfileModal from './components/modals/EditProfileModal';
import ConnectWalletModal from './components/modals/ConnectWalletModal';
import InviteAcceptModal from './components/modals/InviteAcceptModal';
import WelcomeModal from './components/modals/WelcomeModal';
import WalletMigrationModal from './components/modals/WalletMigrationModal';
import HomeView from './components/views/HomeView';
import EditorView from './components/views/EditorView';
import ReaderView from './components/views/ReaderView';
import ProfileView from './components/views/ProfileView';
import AuthorProfileView from './components/views/AuthorProfileView';
import AuthorSettingsView from './components/views/AuthorSettingsView';
import OrganizationSettingsView from './components/views/OrganizationSettingsView';
import Navigation from './components/Navigation';
import ThemeToggle from './components/ThemeToggle';
import { ThemeProvider, useTheme } from './ThemeContext';
import { Spinner } from './components/ui';


function AppContent() {
  const { t, isDark } = useTheme();
  const [view, setView] = useState('home');
  const [posts, setPosts] = useState([]);
  const [currentPost, setCurrentPost] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [authorName, setAuthorName] = useState('');
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [walletCopied, setWalletCopied] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [connectionMessage, setConnectionMessage] = useState('');
  const [selectedWalletType, setSelectedWalletType] = useState(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  
  const [authors, setAuthors] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [primaryAuthorId, setPrimaryAuthorId] = useState(null);
  const [subscribedAuthors, setSubscribedAuthors] = useState([]);
  const [likedPosts, setLikedPosts] = useState([]);
  
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  
  const [showAddAuthorModal, setShowAddAuthorModal] = useState(false);
  const [showAddOrganizationModal, setShowAddOrganizationModal] = useState(false);
  const [showAddLinkModal, setShowAddLinkModal] = useState(false);
  const [showChangeAvatarModal, setShowChangeAvatarModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [inviteCode, setInviteCode] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  const showToast = (message, type = 'error') => {
    setToastMessage({ message, type });
    setTimeout(() => setToastMessage(null), 4000);
  };

  const [newLinkType, setNewLinkType] = useState('X');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);

  const [activeTab, setActiveTab] = useState('POSTS');
  const [drafts, setDrafts] = useState([]);

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('Technology');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const postId = urlParams.get('post');
    if (postId) {
      loadPostById(postId);
      return;
    }
    
    // Check for invite code in path
    const path = window.location.pathname;
    const inviteMatch = path.match(/\/invite\/(.+)/);
    if (inviteMatch) {
      const code = inviteMatch[1];
      setInviteCode(code);
      setShowInviteModal(true);
    }
  }, []);

  const loadPostById = async (postId) => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('id', postId)
        .single();

      if (error) throw error;
      if (data) {
        viewPost(data);
      }
    } catch (error) {
      console.error('Error loading post:', error);
    }
  };

  useEffect(() => {
    if (selectedAuthor) {
      setCurrentUser(selectedAuthor);
      localStorage.setItem('current_author_id', selectedAuthor.id);
    }
  }, [selectedAuthor]);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      const authorId = localStorage.getItem('current_author_id');
      if (!authorId) return;
      
      try {
        const { data } = await supabase
          .from('authors')
          .select('*')
          .eq('id', authorId)
          .single();
        
        if (data) {
          setCurrentUser(data);
          setSelectedAuthor(data);
          // Also set as primary if not already set
          if (!primaryAuthorId) {
            setPrimaryAuthorId(data.id);
          }
        }
      } catch (error) {
        console.error('Error loading current user:', error);
      }
    };
    
    if (walletAddress) {
      fetchCurrentUser();
    }
  }, [walletAddress]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      if (walletAddress) {
        const { data: userData } = await supabase
          .from('users')
          .select('display_name')
          .eq('wallet_address', walletAddress)
          .single();
        
        if (userData) {
          setAuthorName(userData.display_name);
          setEditDisplayName(userData.display_name);
        }
      }

      const { data: postsData, error } = await supabase
        .from('posts')
        .select('*')
        .neq('status', 'draft')
        .or(`scheduled_at.is.null,scheduled_at.lte.${Date.now()}`)
        .order('timestamp', { ascending: false });

      if (error) {
        console.error('Error loading posts:', error);
      } else {
        setPosts(postsData || []);
      }

      // Load drafts for current user
      if (walletAddress) {
        const { data: draftsData } = await supabase
          .from('posts')
          .select('*')
          .eq('wallet_address', walletAddress)
          .eq('status', 'draft')
          .order('updated_at', { ascending: false });
        setDrafts(draftsData || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const savePost = async () => {
    if (!title.trim() || !content.trim()) return;
    if (!walletAddress) {
      showToast('Please connect your wallet first');
      return;
    }
    if (!selectedAuthor) {
      showToast('Please create an author profile first');
      return;
    }

    const post = {
      id: currentPost?.id || `post_${Date.now()}`,
      title: title.trim(),
      subtitle: subtitle.trim(),
      content: content.trim(),
      category,
      author: selectedAuthor.name,
      author_id: selectedAuthor.id,
      wallet_address: walletAddress,
      timestamp: currentPost?.timestamp || Date.now(),
      updated_at: Date.now(),
      views: currentPost?.views || 0,
      like_count: currentPost?.like_count || 0,
      comment_count: currentPost?.comment_count || 0,
      share_count: currentPost?.share_count || 0,
      read_time: Math.ceil(content.trim().split(/\s+/).length / 200)
    };

    try {
      const { error } = await supabase
        .from('posts')
        .upsert(post);

      if (error) throw error;

      await loadData();
      setView('home');
      resetEditor();
    } catch (error) {
      console.error('Error saving post:', error);
      showToast('Failed to save post');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;

      await loadData();
      if (currentPost?.id === postId) {
        setView('home');
        setCurrentPost(null);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      showToast('Failed to delete post');
    }
  };

  const editPost = (post) => {
    setCurrentPost(post);
    setTitle(post.title);
    setSubtitle(post.subtitle);
    setContent(post.content);
    setCategory(post.category);
    setView('editor');
  };

  const viewPost = async (post) => {
    // Drafts open in editor, not reader
    if (post.status === 'draft') {
      setCurrentPost(post);
      setView('editor');
      window.history.pushState({}, '', '/editor');
      return;
    }

    const updatedPost = { ...post, views: (post.views || 0) + 1 };
    try {
      const { error } = await supabase
        .from('posts')
        .update({ views: updatedPost.views })
        .eq('id', post.id);

      if (error) throw error;

      setCurrentPost(updatedPost);
      setView('reader');
      
      window.history.pushState({}, '', `?post=${post.id}`);
      
      await loadData();
    } catch (error) {
      console.error('Error updating views:', error);
    }
  };

  const resetEditor = () => {
    setTitle('');
    setSubtitle('');
    setContent('');
    setCategory('Technology');
    setCurrentPost(null);
  };

  const updateDisplayName = async () => {
    if (!editDisplayName.trim()) return;
    
    try {
      const { error } = await supabase
        .from('users')
        .update({ display_name: editDisplayName.trim() })
        .eq('wallet_address', walletAddress);

      if (error) throw error;

      setAuthorName(editDisplayName.trim());
      setShowEditProfileModal(false);
    } catch (error) {
      console.error('Error updating name:', error);
      showToast('Failed to update name');
    }
  };

  const copyWalletAddress = () => {
    navigator.clipboard.writeText(walletAddress);
    setWalletCopied(true);
    setTimeout(() => setWalletCopied(false), 2000);
  };

  const connectWallet = async (walletName) => {
    setConnectionStatus('connecting');
    setConnectionMessage(`Opening ${walletName === 'lace' ? 'Lace' : 'Eternl'} wallet...`);
    setSelectedWalletType(walletName);

    try {
      let cardano;
      
      if (walletName === 'eternl') {
        cardano = window.cardano?.eternl;
      } else if (walletName === 'lace') {
        cardano = window.cardano?.lace;
      }

      if (!cardano) {
        throw new Error('EXTENSION_NOT_FOUND');
      }

      setConnectionMessage('Please approve the connection in your wallet popup...');

      const api = await cardano.enable();
      
      setConnectionMessage('Getting wallet address...');
      
      const usedAddresses = await api.getUsedAddresses();
      
      if (!usedAddresses || usedAddresses.length === 0) {
        throw new Error('NO_ADDRESSES');
      }
      
      const address = usedAddresses[0];

      setConnectionMessage('Saving to database...');

      const { error } = await supabase
        .from('users')
        .upsert({
          wallet_address: address,
          wallet_name: walletName,
          connected_at: Date.now()
        });

      if (error) throw error;

      setWalletAddress(address);
      setWalletConnected(true);

      localStorage.setItem('limestone_wallet', JSON.stringify({ address, walletName }));

      await loadData();

      setConnectionStatus('success');
      setConnectionMessage('Wallet connected successfully!');
      setShowSuccessAnimation(true);

      setTimeout(() => {
        setShowConnectModal(false);
        setConnectionStatus(null);
        setSelectedWalletType(null);
        setTimeout(() => setShowSuccessAnimation(false), 2000);
      }, 2000);

      console.log('Wallet connected successfully:', address);
    } catch (error) {
      console.error('Error connecting wallet:', error);
      
      setWalletConnected(false);
      setWalletAddress('');
      
      setConnectionStatus('error');
      
      if (error.message === 'EXTENSION_NOT_FOUND') {
        setConnectionMessage(
          `${walletName === 'lace' ? 'Lace' : 'Eternl'} wallet extension not found. Please install it from the Chrome Web Store first.`
        );
      } else if (error.message === 'NO_ADDRESSES') {
        setConnectionMessage('Your wallet appears to be empty. Cardano wallets need at least one transaction to generate an address. Please send a small amount of ADA to your wallet first, then try again.');
      } else if (error.message.includes('User declined') || error.message.includes('rejected')) {
        setConnectionMessage('Connection cancelled. You need to approve the connection request in your wallet popup to continue.');
      } else {
        setConnectionMessage(`Connection failed: ${error.message || 'Unknown error'}. If your wallet is new or empty, try sending a small amount of ADA to it first.`);
      }
      
      setTimeout(() => {
        setConnectionStatus(null);
        setSelectedWalletType(null);
      }, 5000);
    }
  };

  const disconnectWallet = async () => {
    resetAriadne();
    setWalletConnected(false);
    setWalletAddress('');
    setAuthorName('');
    setCurrentUser(null);
    setSelectedAuthor(null);
    localStorage.removeItem('limestone_wallet');
    localStorage.removeItem('current_author_id');
    localStorage.removeItem('limestone_welcome_dismissed');
    setView('home');
    window.history.pushState({}, '', '/');
  };

  useEffect(() => {
    const savedWallet = localStorage.getItem('limestone_wallet');
    if (savedWallet) {
      try {
        const { address } = JSON.parse(savedWallet);
        setWalletAddress(address);
        setWalletConnected(true);
      } catch (error) {
        console.error('Error loading saved wallet:', error);
        localStorage.removeItem('limestone_wallet');
      }
    }
  }, []);

  useEffect(() => {
    if (walletAddress) {
      loadAuthors();
      loadOrganizations();
      loadSubscriptions();
      loadLikedPosts();
    }
  }, [walletAddress]);

  // Load social links after authors are loaded
  useEffect(() => {
    if (authors.length > 0) {
      loadSocialLinks();
    }
  }, [authors]);

  const handlePlusClick = () => {
    if (!walletConnected) {
      setShowConnectModal(true);
    } else if (authors.length === 0) {
      localStorage.removeItem('limestone_welcome_dismissed');
      setShowWelcomeModal(true);
    } else {
      resetEditor();
      setView('editor');
    }
  };

  const loadAuthors = async () => {
    if (!walletAddress) return;
    
    try {
      const { data, error } = await supabase
        .from('authors')
        .select('*')
        .eq('wallet_address', walletAddress);

      if (error) throw error;

      setAuthors(data || []);
      
      // Show welcome modal if no authors exist (and user hasn't dismissed it for this wallet)
      if (!data || data.length === 0) {
        const dismissed = localStorage.getItem('limestone_welcome_dismissed');
        if (dismissed !== walletAddress) {
          setShowWelcomeModal(true);
        }
        return;
      }
      
      if (data && data.length > 0) {
        // Always calculate primary author
        const { data: postsData } = await supabase
          .from('posts')
          .select('author_id');
        
        const postCounts = {};
        postsData?.forEach(post => {
          postCounts[post.author_id] = (postCounts[post.author_id] || 0) + 1;
        });
        
        let primaryAuthor = data[0];
        let maxPosts = postCounts[data[0].id] || 0;
        
        data.forEach(author => {
          const count = postCounts[author.id] || 0;
          if (count > maxPosts) {
            maxPosts = count;
            primaryAuthor = author;
          }
        });
        
        setPrimaryAuthorId(primaryAuthor.id);
        
        // Only set selectedAuthor if not already set
        if (!selectedAuthor) {
          setSelectedAuthor(primaryAuthor);
        }
      }
    } catch (error) {
      console.error('Error loading authors:', error);
    }
  };

  const loadOrganizations = async () => {
    if (!walletAddress) return;
    
    try {
      const { data: seatsData, error: seatsError } = await supabase
        .from('org_seats')
        .select('org_id, role')
        .eq('wallet_address', walletAddress);

      if (seatsError) throw seatsError;

      if (!seatsData || seatsData.length === 0) {
        setOrganizations([]);
        return;
      }

      const orgIds = seatsData.map(seat => seat.org_id);
      const { data: orgsData, error: orgsError } = await supabase
        .from('organizations')
        .select('*')
        .in('id', orgIds);

      if (orgsError) throw orgsError;

      const orgsWithRoles = orgsData.map(org => ({
        ...org,
        userRole: seatsData.find(seat => seat.org_id === org.id)?.role
      }));

      setOrganizations(orgsWithRoles || []);
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  };

  const addSocialLink = async () => {
    if (!newLinkUrl.trim() || !selectedAuthor) return;

    const link = {
      id: `link_${Date.now()}`,
      type: newLinkType,
      url: newLinkUrl.trim(),
      entity_type: 'individual',
      entity_id: selectedAuthor.id,
      created_at: Date.now()
    };

    try {
      const { error } = await supabase
        .from('social_links')
        .insert(link);

      if (error) throw error;

      setSocialLinks([...socialLinks, link]);
      setNewLinkUrl('');
      setShowAddLinkModal(false);
    } catch (error) {
      console.error('Error adding link:', error);
      showToast('Failed to add link');
    }
  };

  const loadSocialLinks = async () => {
    if (!walletAddress) return;
    
    try {
      // Social links are now entity-based, not wallet-based
      // Load links for all authors owned by this wallet
      if (authors.length === 0) return;
      
      const authorIds = authors.map(a => a.id);
      const { data, error } = await supabase
        .from('social_links')
        .select('*')
        .eq('entity_type', 'individual')
        .in('entity_id', authorIds);

      if (error) throw error;

      setSocialLinks(data || []);
    } catch (error) {
      console.error('Error loading links:', error);
    }
  };

  const deleteSocialLink = async (linkId) => {
    try {
      const { error } = await supabase
        .from('social_links')
        .delete()
        .eq('id', linkId);

      if (error) throw error;

      setSocialLinks(socialLinks.filter(l => l.id !== linkId));
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  const subscribeToAuthor = async (authorId) => {
    if (!walletAddress) {
      showToast('Please connect your wallet to subscribe');
      return;
    }

    try {
      const subscription = {
        id: `sub_${Date.now()}`,
        author_id: authorId,
        subscriber_wallet: walletAddress,
        created_at: Date.now()
      };

      const { error } = await supabase
        .from('subscriptions')
        .insert(subscription);

      if (error) throw error;

      setSubscribedAuthors([...subscribedAuthors, authorId]);
    } catch (error) {
      console.error('Error subscribing:', error);
      showToast('Failed to subscribe');
    }
  };

  const unsubscribeFromAuthor = async (authorId) => {
    try {
      const { error } = await supabase
        .from('subscriptions')
        .delete()
        .eq('author_id', authorId)
        .eq('subscriber_wallet', walletAddress);

      if (error) throw error;

      setSubscribedAuthors(subscribedAuthors.filter(id => id !== authorId));
    } catch (error) {
      console.error('Error unsubscribing:', error);
      showToast('Failed to unsubscribe');
    }
  };

  const loadSubscriptions = async () => {
    if (!walletAddress) return;
    
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('author_id')
        .eq('subscriber_wallet', walletAddress);

      if (error) throw error;

      setSubscribedAuthors(data?.map(s => s.author_id) || []);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
  };
  
  const loadLikedPosts = async () => {
    if (!walletAddress) return;
    
    try {
      const fingerprint = localStorage.getItem('user_fingerprint');
      if (!fingerprint) return;
      
      const { data, error } = await supabase
        .from('post_likes')
        .select('post_id')
        .eq('user_fingerprint', fingerprint);

      if (error) throw error;

      setLikedPosts(data?.map(l => l.post_id) || []);
    } catch (error) {
      console.error('Error loading liked posts:', error);
    }
  };

  const getActivityContent = () => {
    const walletPosts = posts.filter(p => p.wallet_address === walletAddress);
    const authorPosts = selectedAuthor
      ? posts.filter(p => p.author_id === selectedAuthor.id)
      : walletPosts;

    switch(activeTab) {
      case 'ALL':
        return walletPosts;
      case 'POSTS':
        return authorPosts;
      case 'DRAFTS':
        return drafts;
      case 'LIKES':
        return likedPosts.length > 0
          ? posts.filter(p => likedPosts.includes(p.id))
          : null;
      case 'COMMENTS':
        return null;
      case 'SHARES':
        return null;
      default:
        return walletPosts;
    }
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Poppins', sans-serif", color: t.text, backgroundColor: t.bg, transition: 'background-color 0.3s, color 0.3s' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&family=Crimson+Pro:wght@700&display=swap');
        
        :root {
          --brown: ${t.accent};
          --bg: ${t.bg};
          --surface: ${t.surface};
          --text: ${t.text};
          --text-secondary: ${t.textSecondary};
          --text-tertiary: ${t.textTertiary};
          --border: ${t.borderStrong};
          --accent: ${t.accent};
          --bg-subtle: ${t.bgSubtle};
        }
        
        /* Override Tailwind stone colors for theme compatibility */
        .text-stone-600 { color: ${t.textSecondary} !important; }
        .text-stone-500 { color: ${t.textSecondary} !important; }
        .text-stone-300 { color: ${t.textTertiary} !important; }
        .bg-stone-100 { background-color: ${t.bgSubtle} !important; }
        .hover\\:bg-stone-100:hover { background-color: ${t.bgSubtle} !important; }
        .border-stone-200 { border-color: ${t.border} !important; }
        .border-stone-300 { border-color: ${t.borderStrong} !important; }
        .placeholder\\:text-stone-300::placeholder { color: ${t.textTertiary} !important; }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .animate-in {
          animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hover-lift {
          transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .hover-lift:hover {
          transform: translateY(-4px);
        }

        .gradient-text {
          color: ${t.text};
          font-family: 'Crimson Pro', serif;
        }

        .serif-display {
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          letter-spacing: -0.02em;
        }

        .mono {
          font-family: 'Poppins', sans-serif;
          font-weight: 500;
        }

        textarea {
          font-family: 'Poppins', sans-serif;
          line-height: 1.8;
        }

        .prose {
          font-size: 1.125rem;
          line-height: 1.8;
        }

        @keyframes successPulse {
          0% { box-shadow: 0 0 0 0 rgba(61, 36, 18, 0.7); }
          50% { box-shadow: 0 0 0 10px rgba(61, 36, 18, 0); }
          100% { box-shadow: 0 0 0 0 rgba(61, 36, 18, 0); }
        }

        .success-animation {
          animation: successPulse 1.5s ease-in-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .notification-slide {
          animation: slideDown 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>

      <Navigation
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        setView={setView}
        walletConnected={walletConnected}
        showSuccessAnimation={showSuccessAnimation}
        handlePlusClick={handlePlusClick}
        currentUser={currentUser}
        authors={authors}
        primaryAuthorId={primaryAuthorId}
        onSelectAuthor={setSelectedAuthor}
        onDisconnect={disconnectWallet}
      />
    
      {view === 'home' && (
        <HomeView 
          posts={posts}
          loading={loading}
          searchQuery={searchQuery}
          viewPost={viewPost}
        />
      )}

      {view === 'profile' && (
        <ProfileView 
          authorName={authorName}
          walletAddress={walletAddress}
          walletCopied={walletCopied}
          copyWalletAddress={copyWalletAddress}
          setShowChangeAvatarModal={setShowChangeAvatarModal}
          setShowEditProfileModal={setShowEditProfileModal}
          setShowAddLinkModal={setShowAddLinkModal}
          socialLinks={socialLinks}
          deleteSocialLink={deleteSocialLink}
          setShowAddAuthorModal={setShowAddAuthorModal}
          authors={authors}
          selectedAuthor={selectedAuthor}
          setSelectedAuthor={setSelectedAuthor}
          organizations={organizations}
          setSelectedOrganization={setSelectedOrganization}
          setShowAddOrganizationModal={setShowAddOrganizationModal}
          posts={posts}
          walletConnected={walletConnected}
          disconnectWallet={disconnectWallet}
          onTransferWallet={() => setShowMigrationModal(true)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          getActivityContent={getActivityContent}
          resetEditor={resetEditor}
          setView={setView}
          viewPost={viewPost}
        />
      )}

      {view === 'editor' && (
        <EditorView 
          supabase={supabase}
          connectedWallet={walletAddress}
          authors={authors}
          setAuthors={setAuthors}
          organizations={organizations}
          currentPost={currentPost}
          setCurrentPost={setCurrentPost}
          setView={setView}
        />
      )}
        
      {view === 'reader' && currentPost && (
        <ReaderView 
          currentPost={currentPost}
          setCurrentPost={setCurrentPost}
          currentUser={currentUser}
          walletAddress={walletAddress}
          subscribedAuthors={subscribedAuthors}
          editPost={editPost}
          deletePost={deletePost}
          subscribeToAuthor={subscribeToAuthor}
          unsubscribeFromAuthor={unsubscribeFromAuthor}
          setView={setView}
          setSelectedAuthor={setSelectedAuthor}
          onShowCommentModal={() => setShowCommentModal(true)}
          onShowShareModal={() => setShowShareModal(true)}
        />
      )}

      {view === 'authorProfile' && selectedAuthor && (
        <AuthorProfileView 
          selectedAuthor={selectedAuthor}
          posts={posts}
          walletAddress={walletAddress}
          subscribedAuthors={subscribedAuthors}
          setView={setView}
          viewPost={viewPost}
          subscribeToAuthor={subscribeToAuthor}
          unsubscribeFromAuthor={unsubscribeFromAuthor}
          setShowConnectModal={setShowConnectModal}
        />
      )}

      {view === 'authorSettings' && selectedAuthor && (
        <AuthorSettingsView
          selectedAuthor={selectedAuthor}
          setView={setView}
          setCurrentPost={setCurrentPost}
          onAuthorUpdated={() => {
            loadAuthors();
            loadData();
          }}
          onAuthorDeleted={() => {
            loadAuthors();
            setSelectedAuthor(null);
          }}
        />
      )}

      {view === 'organizationSettings' && selectedOrganization && (
        <OrganizationSettingsView 
          selectedOrganization={selectedOrganization}
          walletAddress={walletAddress}
          setView={setView}
          onOrganizationUpdated={() => {
            loadOrganizations();
            loadData();
          }}
          onOrganizationDeleted={() => {
            loadOrganizations();
            setSelectedOrganization(null);
          }}
        />
      )}

      {connectionStatus && (
        <div
          className="notification-slide"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: connectionStatus === 'success' ? t.successBg : connectionStatus === 'error' ? t.errorBg : t.surface,
            border: `2px solid ${connectionStatus === 'success' ? t.success : connectionStatus === 'error' ? t.error : t.borderStrong}`,
            borderRadius: '12px',
            padding: '24px 32px',
            zIndex: 2000,
            minWidth: '300px',
            maxWidth: '500px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}
        >
          {connectionStatus === 'connecting' && (
            <div>
              <div style={{ margin: '0 auto 16px', width: '40px' }}>
                <Spinner size={40} />
              </div>
              <p style={{ fontSize: '16px', fontWeight: '600', color: t.text, marginBottom: '8px' }}>
                Connecting...
              </p>
              <p style={{ fontSize: '14px', color: t.textSecondary }}>
                {connectionMessage}
              </p>
            </div>
          )}

          {connectionStatus === 'success' && (
            <div>
              <div style={{
                width: '48px',
                height: '48px',
                backgroundColor: t.success,
                borderRadius: '50%',
                margin: '0 auto 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: t.surface,
                fontSize: '24px'
              }}>
                ✓
              </div>
              <p style={{ fontSize: '18px', fontWeight: '600', color: t.success, marginBottom: '8px' }}>
                Success!
              </p>
              <p style={{ fontSize: '14px', color: t.textSecondary }}>
                {connectionMessage}
              </p>
            </div>
          )}

          {connectionStatus === 'error' && (
            <div>
              <div style={{
                width: '48px',
                height: '48px',
                backgroundColor: t.error,
                borderRadius: '50%',
                margin: '0 auto 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: t.surface,
                fontSize: '24px'
              }}>
                ✕
              </div>
              <p style={{ fontSize: '18px', fontWeight: '600', color: t.error, marginBottom: '8px' }}>
                Connection Failed
              </p>
              <p style={{ fontSize: '14px', color: t.textSecondary, marginBottom: '16px' }}>
                {connectionMessage}
              </p>
              <button
                onClick={() => {
                  setConnectionStatus(null);
                  setSelectedWalletType(null);
                }}
                style={{
                  backgroundColor: t.buttonBg,
                  color: t.buttonText,
                  padding: '8px 24px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif'
                }}
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      )}

      <ConnectWalletModal 
        show={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        connectionStatus={connectionStatus}
        connectionMessage={connectionMessage}
        selectedWalletType={selectedWalletType}
        onConnectWallet={connectWallet}
      />

      <AddAuthorModal 
        show={showAddAuthorModal}
        onClose={() => setShowAddAuthorModal(false)}
        walletAddress={walletAddress}
        onAuthorCreated={() => {
          loadAuthors();
          setShowAddAuthorModal(false);
        }}
      />

      <AddOrganizationModal 
        show={showAddOrganizationModal}
        onClose={() => setShowAddOrganizationModal(false)}
        walletAddress={walletAddress}
        onOrganizationCreated={() => {
          loadOrganizations();
          setShowAddOrganizationModal(false);
        }}
      />

      <AddLinkModal 
        show={showAddLinkModal}
        onClose={() => {
          setShowAddLinkModal(false);
          setNewLinkUrl('');
        }}
        newLinkType={newLinkType}
        setNewLinkType={setNewLinkType}
        newLinkUrl={newLinkUrl}
        setNewLinkUrl={setNewLinkUrl}
        onAddLink={addSocialLink}
      />

      <ChangeAvatarModal 
        show={showChangeAvatarModal}
        onClose={() => setShowChangeAvatarModal(false)}
        avatarFile={avatarFile}
        setAvatarFile={setAvatarFile}
      />

      <EditProfileModal 
        show={showEditProfileModal}
        onClose={() => setShowEditProfileModal(false)}
        editDisplayName={editDisplayName}
        setEditDisplayName={setEditDisplayName}
        onUpdateDisplayName={updateDisplayName}
        authorName={authorName}
      />

      <CommentModal
        show={showCommentModal}
        onClose={() => setShowCommentModal(false)}
        currentPost={currentPost}
        currentUser={currentUser}
        setShowConnectModal={setShowConnectModal}
        onCommentAdded={() => {
          loadData();
        }}
      />

      <ShareModal 
        show={showShareModal}
        onClose={() => setShowShareModal(false)}
        currentPost={currentPost}
      />

      <InviteAcceptModal 
        show={showInviteModal}
        inviteCode={inviteCode}
        walletAddress={walletAddress}
        walletConnected={walletConnected}
        authors={authors}
        onConnectWallet={() => setShowConnectModal(true)}
        onAccept={(org) => {
          setShowInviteModal(false);
          loadOrganizations();
          setView('profile');
          window.history.pushState({}, '', '/');
        }}
        onDecline={() => {
          setShowInviteModal(false);
          setInviteCode(null);
          window.history.pushState({}, '', '/');
        }}
        supabase={supabase}
      />

      <WelcomeModal
        show={showWelcomeModal}
        walletAddress={walletAddress}
        onClose={() => setShowWelcomeModal(false)}
        onAuthorCreated={() => {
          setShowWelcomeModal(false);
          loadAuthors();
        }}
        onDisconnect={disconnectWallet}
      />

      <WalletMigrationModal
        show={showMigrationModal}
        onClose={() => setShowMigrationModal(false)}
        walletAddress={walletAddress}
        onMigrationComplete={(newAddress) => {
          setShowMigrationModal(false);
          disconnectWallet();
        }}
      />

      <ThemeToggle />

      {toastMessage && (
        <div
          className="notification-slide"
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: toastMessage.type === 'error' ? t.errorBg : t.surface,
            border: `1px solid ${toastMessage.type === 'error' ? t.error : t.borderStrong}`,
            color: toastMessage.type === 'error' ? t.error : t.text,
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '14px',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: '500',
            zIndex: 3000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            cursor: 'pointer'
          }}
          onClick={() => setToastMessage(null)}
        >
          {toastMessage.message}
        </div>
      )}

    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}